import { Controller } from "@hotwired/stimulus"

// Limits a <trix-editor> (or plain textarea) to `maxLengthValue`
// characters and mirrors the remaining count into a count target.
//
// Usage:
//   <div data-controller="trix-limit"
//        data-trix-limit-max-length-value="600">
//     <trix-editor data-trix-limit-target="editor"
//                  data-action="keydown->trix-limit#handleKeydown
//                               paste->trix-limit#handlePaste"></trix-editor>
//     <span data-trix-limit-target="count">600</span>
//   </div>
export default class extends Controller {
  static targets = ["editor", "count"]

  static values = {
    maxLength: { type: Number, default: 600 }
  }

  connect() {
    this.update = this.update.bind(this)
    this.update()
    this.editorTarget.addEventListener("trix-initialize", this.update)
    this.editorTarget.addEventListener("trix-change", this.update)
    this.editorTarget.addEventListener("input", this.update)
  }

  disconnect() {
    this.editorTarget.removeEventListener("trix-initialize", this.update)
    this.editorTarget.removeEventListener("trix-change", this.update)
    this.editorTarget.removeEventListener("input", this.update)
  }

  handleKeydown(event) {
    const allowedKeys = [
      "Backspace", "Delete", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown",
      "Tab", "Home", "End"
    ]

    if (allowedKeys.includes(event.key) || event.metaKey || event.ctrlKey || event.altKey) return

    const insertsCharacter = event.key.length === 1 || event.key === "Enter"
    if (!insertsCharacter) return

    if (this.plainTextLength() >= this.maxLengthValue) {
      event.preventDefault()
    }
  }

  handlePaste(event) {
    const pasted = event.clipboardData?.getData("text/plain") || ""
    const remaining = this.maxLengthValue - this.plainTextLength()

    if (remaining <= 0) {
      event.preventDefault()
      return
    }

    if (pasted.length > remaining) {
      event.preventDefault()
      this.editorTarget.editor.insertString(pasted.slice(0, remaining))
    }
  }

  update() {
    if (this.truncating) return
    this.enforceLimit()
    if (this.hasCountTarget) {
      const remaining = Math.max(this.maxLengthValue - this.plainTextLength(), 0)
      this.countTarget.textContent = remaining
    }
  }

  // If the content somehow got past the max (paste, IME, programmatic insert),
  // delete the overflow. Guarded by `this.truncating` to avoid the trix-change
  // the delete itself fires from re-entering this method.
  enforceLimit() {
    const editor = this.editorTarget.editor
    if (!editor) return
    const length = editor.getDocument().toString().length
    if (length <= this.maxLengthValue) return

    this.truncating = true
    try {
      editor.setSelectedRange([this.maxLengthValue, length])
      editor.deleteInDirection("forward")
    } finally {
      this.truncating = false
    }
  }

  plainTextLength() {
    try {
      return this.editorTarget.editor.getDocument().toString().length
    } catch (_) {
      return (this.editorTarget.innerText || "").length
    }
  }
}
